// The colour of the browser's bars.
//
// Most browsers (Chrome on Android, Safari before iOS 26) take it from
// <meta name="theme-color">: kept up to date with the page on screen.
//
// Safari 26 ignores that tag. How it picks the colour (WebKit,
// LocalFrameView::fixedContainerEdges + WebPage::willCommitMainFrameData):
//   - for each bar it hit-tests a point 4px inside that edge of the screen and
//     takes the first fixed (or sticky) element there that spans 90%+ of the
//     width, is taller than 10px and has an opacity of 0.1 or more: its
//     background colour, made opaque
//   - otherwise it falls back to (a sample of) the page background
//   - it only looks again when a fixed element is added or removed, the
//     screen size changes or a page loads: changing a colour in place is not
//     picked up, which is why the bars kept the previous page's colour after
//     an in-app page change
// So: one strip on each edge, above everything, 12px high and pushed 7px off
// screen (5px left on screen, at 12% opacity in the page's own colour: not
// visible against the page), carrying the colour of the page on screen. When
// the colour changes the strip is replaced by a new one, which makes Safari
// look again; during a page change that happens every frame, so the bars
// change along with the background coming up.

type RGB = number[]
const EDGES = ["top", "bottom"] as const
const strips: Partial<Record<(typeof EDGES)[number], HTMLElement>> = {}
let now: { top: RGB; bottom: RGB } = { top: [255, 255, 255], bottom: [255, 255, 255] }
let painted = ""

const css = (c: RGB) => `rgb(${c.map((v) => Math.round(v)).join(",")})`

function strip(edge: (typeof EDGES)[number], color: string) {
    const el = document.createElement("div")
    el.setAttribute("aria-hidden", "true")
    el.className = "x-bar"
    el.style.cssText = `position:fixed;left:0;${edge}:-7px;width:100%;height:12px;opacity:.12;pointer-events:none;z-index:2147483647;background-color:${color}`
    return el
}

let shown: { top: RGB; bottom: RGB } = now
function paint(t: RGB, b: RGB) {
    shown = { top: t, bottom: b }
    const colors = { top: css(t), bottom: css(b) }
    const key = colors.top + colors.bottom
    if (key === painted) return
    painted = key
    for (const edge of EDGES) {
        // a new element, not a new colour: that's what makes Safari look again
        const next = strip(edge, colors[edge])
        const old = strips[edge]
        if (old) old.replaceWith(next)
        else document.body.appendChild(next)
        strips[edge] = next
    }
    // the fallback when nothing else is found, and the theme colour for others
    document.documentElement.style.backgroundColor = colors.top
    let meta = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement | null
    if (!meta) {
        meta = document.createElement("meta")
        meta.name = "theme-color"
        document.head.appendChild(meta)
    }
    meta.content = colors.top
}

/** the page's background as [r, g, b, position] stops: its top and bottom colours */
const ends = (stops: number[][]) => ({ top: stops[0].slice(0, 3), bottom: stops[stops.length - 1].slice(0, 3) })

/** a page is on screen */
export function setBars(stops: number[][]) {
    cancelAnimationFrame(fade)
    now = ends(stops)
    paint(now.top, now.bottom)
}

let fade = 0
/** The colours change on the same page (another case scrolled in).
 *  follow: how far the new background is up (0..1) for the bars this frame,
 *  from the code fading it in (already a frame ahead, for Safari's own fade);
 *  null once it's done. Without it: over ms. */
export function fadeBars(stops: number[][], ms = 600, follow?: () => number | null) {
    cancelAnimationFrame(fade)
    const t0 = performance.now()
    const from = { ...shown }
    const step = (t: number) => {
        let e: number | null
        if (follow) e = follow()
        else {
            const k = Math.min(1, (t - t0) / ms)
            e = k < 1 ? (k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2) : null
        }
        if (e == null || t - t0 > ms * 4) return setBars(stops)
        now = from
        mix(stops, e)
        fade = requestAnimationFrame(step)
    }
    fade = requestAnimationFrame(step)
}

/** during a page change: d = 0..1 of the next page's background coming up */
export function mixBars(stops: number[][], d: number) {
    if (fade) {
        // a page change takes over from a case change still running, from
        // the colours shown right now
        cancelAnimationFrame(fade)
        fade = 0
        now = shown
    }
    mix(stops, d)
}
function mix(stops: number[][], d: number) {
    const to = ends(stops)
    const lerp = (a: RGB, b: RGB) => a.map((v, i) => v + (b[i] - v) * d)
    paint(lerp(now.top, to.top), lerp(now.bottom, to.bottom))
}
